import { useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router';

import { useAuthStore } from './net/auth-store';
import { router } from './router';
import { restoreActiveCharacter } from './sim/restore-active-character';

/**
 * Fire {@link restoreActiveCharacter} exactly once per mount, restoring the
 * last-active character so a page reload no longer drops the player back to
 * "No active character" (Step 10d-ux-polish).
 *
 * StrictMode double-invokes the effect in dev, so the restore may run twice;
 * both runs are read-only and its `active === null` re-check makes a duplicate
 * activate a no-op.
 */
function useRestoreActiveCharacter(): void {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void restoreActiveCharacter();
  }, []);
}

/**
 * Resolve the auth session exactly once per mount (P6-5). In offline builds
 * (no VITE_API_BASE_URL) this settles to 'offline' without any network call.
 */
function useBootstrapAuth(): void {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void useAuthStore.getState().bootstrap();
  }, []);
}

/**
 * Thin entrypoint — every visible UI lives under the router-driven
 * Layout. Phase 3 Step 6 (2/4); Step 10d adds boot-time active restore.
 */
export function App() {
  useRestoreActiveCharacter();
  useBootstrapAuth();
  return <RouterProvider router={router} />;
}
